import { ConsultantNode } from '../../types/career';
import { CanvasEdgeLayout, CanvasNodeLayout } from '../../types/canvas';
import { getNetRateDifference, getRateDifference } from '../../math/rates';

export interface LayoutResult {
  nodes: CanvasNodeLayout[];
  edges: CanvasEdgeLayout[];
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

const BASE_RADIUS_STEP = 210;
const NODE_WIDTH = 96;
const NODE_HEIGHT = 96;
const MIN_ARC_SEPARATION = 124; // Minimum arc distance between node centers along a ring
const MIN_CENTER_DISTANCE = 96; // Absolute minimum Euclidean distance between any two node centers

function getVisibleChildren(node: ConsultantNode, hideDormant: boolean): ConsultantNode[] {
  return hideDormant ? node.children.filter((c) => c.isActive) : node.children;
}

function countNodesPerDepth(
  node: ConsultantNode,
  depth: number,
  depthCounts: Map<number, number>,
  hideDormant: boolean
) {
  depthCounts.set(depth, (depthCounts.get(depth) || 0) + 1);
  const visibleChildren = getVisibleChildren(node, hideDormant);
  for (const child of visibleChildren) {
    countNodesPerDepth(child, depth + 1, depthCounts, hideDormant);
  }
}

function getMaxDepth(node: ConsultantNode, currentDepth: number, hideDormant: boolean): number {
  const visibleChildren = getVisibleChildren(node, hideDormant);
  if (visibleChildren.length === 0) return currentDepth;
  let maxD = currentDepth;
  for (const child of visibleChildren) {
    const d = getMaxDepth(child, currentDepth + 1, hideDormant);
    if (d > maxD) maxD = d;
  }
  return maxD;
}

/**
 * Computes concentric radial polar layout coordinates for a consultant tree.
 * Prevents node overlaps at outer rings by dynamically scaling radii and allocating
 * angular wedges based on recursive subtree demand.
 */
export function calculateRadialLayout(root: ConsultantNode, hideDormant: boolean = false): LayoutResult {
  const nodes: CanvasNodeLayout[] = [];
  const edges: CanvasEdgeLayout[] = [];

  const maxDepth = getMaxDepth(root, 0, hideDormant);

  // Root node is always at origin
  nodes.push({
    id: root.id,
    data: root,
    x: 0,
    y: 0,
    radius: 0,
    angle: 0,
    depth: 0,
    isRoot: true,
    parentId: null,
  });

  const rootChildren = getVisibleChildren(root, hideDormant);
  if (rootChildren.length === 0 || maxDepth === 0) {
    return {
      nodes,
      edges,
      bounds: {
        minX: -NODE_WIDTH,
        maxX: NODE_WIDTH,
        minY: -NODE_HEIGHT,
        maxY: NODE_HEIGHT,
      },
    };
  }

  // 1. Count nodes at each depth level
  const depthCounts = new Map<number, number>();
  countNodesPerDepth(root, 0, depthCounts, hideDormant);

  // 2. Initial baseline radii per depth level
  const radiusByDepth: number[] = [0];
  for (let d = 1; d <= maxDepth; d++) {
    const countAtDepth = depthCounts.get(d) || 1;
    const minCircumference = countAtDepth * MIN_ARC_SEPARATION;
    const minRadiusForCircumference = Math.ceil(minCircumference / (2 * Math.PI));
    const previousRadius = radiusByDepth[d - 1];
    const computedRadius = Math.max(
      previousRadius + BASE_RADIUS_STEP,
      minRadiusForCircumference,
      190 + d * 150
    );
    radiusByDepth.push(computedRadius);
  }

  // 3. Bottom-up calculation of required angular span per subtree
  function computeSubtreeAngularDemand(
    node: ConsultantNode,
    depth: number,
    radii: number[],
    demandMap: Map<string, number>
  ): number {
    const currentRadius = radii[depth] || (depth * BASE_RADIUS_STEP);
    const selfAngularDemand = MIN_ARC_SEPARATION / currentRadius;

    const visibleChildren = getVisibleChildren(node, hideDormant);
    if (visibleChildren.length === 0) {
      demandMap.set(node.id, selfAngularDemand);
      return selfAngularDemand;
    }

    let childrenDemandSum = 0;
    for (const child of visibleChildren) {
      childrenDemandSum += computeSubtreeAngularDemand(child, depth + 1, radii, demandMap);
    }

    const effectiveDemand = Math.max(selfAngularDemand, childrenDemandSum);
    demandMap.set(node.id, effectiveDemand);
    return effectiveDemand;
  }

  let demandMap = new Map<string, number>();
  let totalRootDemand = 0;
  for (const child of rootChildren) {
    totalRootDemand += computeSubtreeAngularDemand(child, 1, radiusByDepth, demandMap);
  }

  // 4. If total angular demand exceeds full circumference (with safety margin), dynamically scale radii
  const targetAvailableAngle = 2 * Math.PI * 0.95;
  if (totalRootDemand > targetAvailableAngle) {
    const scaleFactor = totalRootDemand / targetAvailableAngle;
    for (let d = 1; d <= maxDepth; d++) {
      radiusByDepth[d] = Math.round(radiusByDepth[d] * scaleFactor);
    }

    // Recompute demand with scaled radii
    demandMap = new Map<string, number>();
    totalRootDemand = 0;
    for (const child of rootChildren) {
      totalRootDemand += computeSubtreeAngularDemand(child, 1, radiusByDepth, demandMap);
    }
  }

  // 5. Top-down recursive placement of subtrees within allocated angular wedges
  const totalCircleSpan = 2 * Math.PI;

  function layoutSubtreeChildren(
    parentNode: ConsultantNode,
    parentX: number,
    parentY: number,
    wedgeStart: number,
    wedgeSpan: number,
    depth: number
  ) {
    const visibleChildren = getVisibleChildren(parentNode, hideDormant);
    if (visibleChildren.length === 0) return;

    let totalChildrenDemand = 0;
    for (const child of visibleChildren) {
      totalChildrenDemand += demandMap.get(child.id) || (MIN_ARC_SEPARATION / radiusByDepth[depth]);
    }

    const currentRingRadius = radiusByDepth[depth];
    let currentAngle = wedgeStart;

    for (let i = 0; i < visibleChildren.length; i++) {
      const child = visibleChildren[i];
      const childDemand = demandMap.get(child.id) || (MIN_ARC_SEPARATION / currentRingRadius);
      const childWedgeSpan = totalChildrenDemand > 0
        ? (childDemand / totalChildrenDemand) * wedgeSpan
        : wedgeSpan / visibleChildren.length;

      const childAngle = currentAngle + childWedgeSpan / 2;
      const childX = Math.round(currentRingRadius * Math.cos(childAngle));
      const childY = Math.round(currentRingRadius * Math.sin(childAngle));

      nodes.push({
        id: child.id,
        data: child,
        x: childX,
        y: childY,
        radius: currentRingRadius,
        angle: childAngle,
        depth,
        isRoot: false,
        parentId: parentNode.id,
      });

      const rateDiff = getRateDifference(parentNode.level, child.level);
      const netRateDiff = getNetRateDifference(parentNode.level, child.level);
      const commission = child.totalMonthlyAE * netRateDiff;

      edges.push({
        sourceId: parentNode.id,
        targetId: child.id,
        sourceX: parentX,
        sourceY: parentY,
        targetX: childX,
        targetY: childY,
        mentorLevel: parentNode.level,
        branchLevel: child.level,
        rateDifferenceUAH: rateDiff,
        netRateDifferenceUAH: netRateDiff,
        monthlyTurnoverAE: child.totalMonthlyAE,
        lineCommissionUAH: commission,
        sourceIsRoot: parentNode.id === root.id,
      });

      // Recurse into downlines within this child's allocated wedge
      layoutSubtreeChildren(
        child,
        childX,
        childY,
        currentAngle,
        childWedgeSpan,
        depth + 1
      );

      currentAngle += childWedgeSpan;
    }
  }

  // Place root children around the circle
  let startAngle = 0;
  for (const child of rootChildren) {
    const childDemand = demandMap.get(child.id) || (MIN_ARC_SEPARATION / radiusByDepth[1]);
    const childWedgeSpan = (childDemand / totalRootDemand) * totalCircleSpan;
    const childAngle = startAngle + childWedgeSpan / 2;
    const r1 = radiusByDepth[1];
    const childX = Math.round(r1 * Math.cos(childAngle));
    const childY = Math.round(r1 * Math.sin(childAngle));

    nodes.push({
      id: child.id,
      data: child,
      x: childX,
      y: childY,
      radius: r1,
      angle: childAngle,
      depth: 1,
      isRoot: false,
      parentId: root.id,
    });

    const rateDiff = getRateDifference(root.level, child.level);
    const netRateDiff = getNetRateDifference(root.level, child.level);
    const commission = child.totalMonthlyAE * netRateDiff;

    edges.push({
      sourceId: root.id,
      targetId: child.id,
      sourceX: 0,
      sourceY: 0,
      targetX: childX,
      targetY: childY,
      mentorLevel: root.level,
      branchLevel: child.level,
      rateDifferenceUAH: rateDiff,
      netRateDifferenceUAH: netRateDiff,
      monthlyTurnoverAE: child.totalMonthlyAE,
      lineCommissionUAH: commission,
      sourceIsRoot: true,
    });

    layoutSubtreeChildren(
      child,
      childX,
      childY,
      startAngle,
      childWedgeSpan,
      2
    );

    startAngle += childWedgeSpan;
  }

  // 6. Angular relaxation pass per depth level to prevent adjacent node crowding
  const nodesByDepth = new Map<number, CanvasNodeLayout[]>();
  for (const node of nodes) {
    if (node.isRoot) continue;
    const list = nodesByDepth.get(node.depth) || [];
    list.push(node);
    nodesByDepth.set(node.depth, list);
  }

  nodesByDepth.forEach((depthNodes, d) => {
    if (depthNodes.length <= 1) return;
    const ringRadius = radiusByDepth[d];
    if (!ringRadius || ringRadius <= 0) return;

    // Minimum angular separation required for center distance >= 100px
    const minAngleDelta = (MIN_CENTER_DISTANCE + 4) / ringRadius;

    // Normalize angles to [0, 2*PI) before sorting to prevent wraparound inversion
    for (const node of depthNodes) {
      node.angle = ((node.angle! % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    }

    depthNodes.sort((a, b) => a.angle! - b.angle!);

    const len = depthNodes.length;
    for (let pass = 0; pass < 6; pass++) {
      let anyShift = false;
      for (let i = 0; i < len; i++) {
        const nextIdx = (i + 1) % len;
        const cur = depthNodes[i];
        const next = depthNodes[nextIdx];

        let diff = next.angle! - cur.angle!;
        if (nextIdx === 0) {
          diff += 2 * Math.PI;
        }

        if (diff < minAngleDelta) {
          anyShift = true;
          const deficit = minAngleDelta - diff;
          cur.angle = cur.angle! - deficit * 0.5;
          next.angle = next.angle! + deficit * 0.5;
        }
      }
      if (!anyShift) break;
    }

    // Update (x, y) coordinates from relaxed angles
    for (const node of depthNodes) {
      node.x = Math.round(ringRadius * Math.cos(node.angle!));
      node.y = Math.round(ringRadius * Math.sin(node.angle!));
    }
  });

  // 7. Ring-adjacent Euclidean relaxation safety pass: guarantees distance >= MIN_CENTER_DISTANCE (96px)
  // Nodes on different depth rings have radial difference >= BASE_RADIUS_STEP (210px > 96px) and can
  // never collide across rings. Checking adjacent neighbors along each ring provides O(N) safety.
  nodesByDepth.forEach((depthNodes) => {
    const len = depthNodes.length;
    if (len <= 1) return;
    for (let i = 0; i < len; i++) {
      const nextIdx = (i + 1) % len;
      const u = depthNodes[i];
      const v = depthNodes[nextIdx];
      const dx = v.x - u.x;
      const dy = v.y - u.y;
      const dist = Math.hypot(dx, dy);

      if (dist < MIN_CENTER_DISTANCE) {
        const overlap = MIN_CENTER_DISTANCE - dist + 2;
        const nx = dist > 0.001 ? dx / dist : 1;
        const ny = dist > 0.001 ? dy / dist : 0;

        u.x = Math.round(u.x - nx * overlap * 0.5);
        u.y = Math.round(u.y - ny * overlap * 0.5);
        v.x = Math.round(v.x + nx * overlap * 0.5);
        v.y = Math.round(v.y + ny * overlap * 0.5);
      }
    }
  });

  // 8. Re-synchronize edge endpoints to matching node coordinates
  const nodeMap = new Map<string, CanvasNodeLayout>();
  for (const n of nodes) {
    nodeMap.set(n.id, n);
  }

  for (const edge of edges) {
    const src = nodeMap.get(edge.sourceId);
    const tgt = nodeMap.get(edge.targetId);
    if (src) {
      edge.sourceX = src.x;
      edge.sourceY = src.y;
    }
    if (tgt) {
      edge.targetX = tgt.x;
      edge.targetY = tgt.y;
    }
  }

  // 9. Compute enclosing bounding box with safety padding
  let minX = -NODE_WIDTH;
  let maxX = NODE_WIDTH;
  let minY = -NODE_HEIGHT;
  let maxY = NODE_HEIGHT;

  for (const n of nodes) {
    minX = Math.min(minX, n.x - NODE_WIDTH / 2 - 30);
    maxX = Math.max(maxX, n.x + NODE_WIDTH / 2 + 30);
    minY = Math.min(minY, n.y - NODE_HEIGHT / 2 - 30);
    maxY = Math.max(maxY, n.y + NODE_HEIGHT / 2 + 30);
  }

  return {
    nodes,
    edges,
    bounds: { minX, maxX, minY, maxY },
  };
}
